<template>
  <div class="users-list-page">
    <v-container fluid grid-list-xl>
      <v-layout row wrap>
        <v-flex xs12>
          <div class="list-hero">
            <p class="list-crumb">Usuarios / Listado</p>
            <h1 class="list-title">Listado de Usuarios</h1>
            <p class="list-subtitle">
              Consulte y administre todos los usuarios del sistema.
            </p>
          </div>
        </v-flex>

        <v-flex xs12>
          <v-card class="users-card">
            <v-card-text class="users-card__toolbar">
              <v-layout row wrap>
                <v-flex md8 sm7 xs12>
                  <v-text-field
                    v-model="search"
                    class="list-search"
                    append-icon="search"
                    clearable
                    hide-details
                    placeholder="Buscar por nombre, apellido o email..."
                    solo
                    @keyup.enter="onSearch"
                    @click:append="onSearch"
                  ></v-text-field>
                </v-flex>

                <v-flex md3 sm5 xs12>
                  <v-select
                    v-model="selectedRole"
                    :items="roleOptions"
                    class="list-role-filter"
                    hide-details
                    item-text="text"
                    item-value="value"
                    label="Rol"
                    solo
                    @change="onSearch"
                  ></v-select>
                </v-flex>

                <v-flex md1 sm2 xs12 class="toolbar-actions">
                  <v-btn
                    icon
                    class="btn-download"
                    title="Exportar Excel"
                    :loading="exporting"
                    :disabled="exporting"
                    @click="onDownload"
                  >
                    <v-icon small>cloud_download</v-icon>
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-card-text>

            <v-card-text class="users-card__content hidden-sm-and-down">
              <div class="table-wrap">
                <table class="users-table">
                  <thead>
                    <tr>
                      <th>
                        <button
                          class="th-sort"
                          type="button"
                          @click="changeSort('firstName')"
                        >
                          Nombre
                          <v-icon small class="th-sort__icon">{{
                            sortIcon('firstName')
                          }}</v-icon>
                        </button>
                      </th>
                      <th>Apellido</th>
                      <th>Email</th>
                      <th>
                        <button
                          class="th-sort"
                          type="button"
                          @click="changeSort('role')"
                        >
                          Rol
                          <v-icon small class="th-sort__icon">{{
                            sortIcon('role')
                          }}</v-icon>
                        </button>
                      </th>
                      <th class="th-actions">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="user in displayUsers" :key="user.id">
                      <td>{{ user.firstName }}</td>
                      <td>{{ user.lastName }}</td>
                      <td>{{ user.email }}</td>
                      <td>
                        <span :class="['role-chip', roleClass(user)]">{{
                          getRoleName(user)
                        }}</span>
                      </td>
                      <td>
                        <div class="action-group">
                          <v-btn
                            class="btn-edit"
                            icon
                            small
                            :to="`/usuarios/editar/${user.id}`"
                            title="Editar"
                          >
                            <v-icon small>edit</v-icon>
                          </v-btn>
                          <v-btn
                            class="btn-delete"
                            icon
                            small
                            @click="openDisableModal(user.id)"
                            title="Eliminar"
                          >
                            <v-icon small>delete_outline</v-icon>
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="!pagination.loading && displayUsers.length === 0">
                      <td colspan="5" class="empty-state">
                        No se encontraron usuarios con los filtros aplicados.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-card-text>

            <v-card-text class="users-card__content-mobile hidden-md-and-up">
              <div
                v-if="!pagination.loading && displayUsers.length === 0"
                class="mobile-empty"
              >
                No se encontraron usuarios con los filtros aplicados.
              </div>
              <v-layout row wrap>
                <v-flex
                  xs12
                  v-for="user in displayUsers"
                  :key="`card-${user.id}`"
                >
                  <v-card class="user-mobile-card">
                    <v-card-text>
                      <div class="mobile-row">
                        <span class="mobile-label">Nombre</span>
                        <span class="mobile-value"
                          >{{ user.firstName }} {{ user.lastName }}</span
                        >
                      </div>
                      <div class="mobile-row">
                        <span class="mobile-label">Email</span>
                        <span class="mobile-value">{{ user.email }}</span>
                      </div>
                      <div class="mobile-row">
                        <span class="mobile-label">Rol</span>
                        <span class="mobile-value"
                          ><span :class="['role-chip', roleClass(user)]">{{
                            getRoleName(user)
                          }}</span></span
                        >
                      </div>
                      <div class="mobile-actions">
                        <v-btn
                          class="btn-edit"
                          icon
                          small
                          :to="`/usuarios/editar/${user.id}`"
                          title="Editar"
                        >
                          <v-icon small>edit</v-icon>
                        </v-btn>
                        <v-btn
                          class="btn-delete"
                          icon
                          small
                          @click="openDisableModal(user.id)"
                          title="Eliminar"
                        >
                          <v-icon small>delete_outline</v-icon>
                        </v-btn>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>

        <v-flex xs12 v-if="pagination.pages">
          <div class="list-footer">
            <div class="list-footer__count">
              Mostrando {{ displayUsers.length }} de
              {{ pagination.totalItems }} usuarios
            </div>
            <v-pagination
              v-model="pagination.page"
              :length="pagination.pages"
              :total-visible="7"
              @input="asyncPagination"
            ></v-pagination>
          </div>
        </v-flex>
      </v-layout>
    </v-container>

    <v-dialog max-width="350" persistent v-model="loading">
      <loading></loading>
    </v-dialog>

    <delete-confirm-dialog
      v-model="userDisable.modal"
      :max-width="460"
      title="Eliminar usuario"
      subtitle="Esta acción no se puede deshacer"
      message="Vas a eliminar este usuario del sistema."
      warning="El usuario perderá acceso de forma permanente."
      confirm-text="Sí, eliminar"
      @confirm="disableUser"
    ></delete-confirm-dialog>

    <v-snackbar
      v-model="snack.show"
      :color="snack.color"
      :timeout="4000"
      bottom
      right
    >
      {{ snack.message }}
      <v-btn flat icon @click="snack.show = false"
        ><v-icon>close</v-icon></v-btn
      >
    </v-snackbar>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import loading from './../dialog/DialogLoading'
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog'

export default {
  components: {
    loading,
    DeleteConfirmDialog,
  },
  data() {
    return {
      search: '',
      selectedRole: 'ALL',
      rolesCatalog: [],
      loading: true,
      userDisable: {
        modal: false,
        id: null,
      },
      exporting: false,
      snack: { show: false, color: '', message: '' },
      sortBy: 'firstName',
      sortDirection: 'asc',
      pagination: {
        page: 1,
        rpp: this.$config.resultsPerPage,
        totalItems: 0,
        loading: true,
        pages: 0,
        search: {},
      },
    }
  },
  created() {
    this.loadRoles()
    this.asyncPagination()
  },
  computed: {
    ...mapState({
      users: state => state.users.all,
    }),
    roleOptions() {
      const roleMap = new Map()

      ;(this.rolesCatalog || []).forEach(role => {
        if (!role || role.id == null || !role.name) return
        if (this.isExcludedRoleName(role.name)) return
        if (!roleMap.has(role.id)) {
          roleMap.set(role.id, { text: role.name, value: role.id })
        }
      })

      const roles = Array.from(roleMap.values()).sort((a, b) =>
        String(a.text).localeCompare(String(b.text), 'es', {
          sensitivity: 'base',
        })
      )

      return [{ text: 'Todos', value: 'ALL' }, ...roles]
    },
    displayUsers() {
      const source = (Array.isArray(this.users) ? this.users : []).filter(
        user => !this.isExcludedRoleName(this.getRoleName(user))
      )

      return source.sort((a, b) => {
        let first, second
        if (this.sortBy === 'role') {
          first = this.getRoleName(a)
          second = this.getRoleName(b)
        } else {
          first = a[this.sortBy]
          second = b[this.sortBy]
        }
        const compare = String(first || '').localeCompare(
          String(second || ''),
          'es',
          { sensitivity: 'base' }
        )
        return this.sortDirection === 'asc' ? compare : compare * -1
      })
    },
  },
  methods: {
    loadRoles() {
      return this.$store.dispatch('users/getRoles').then(response => {
        const items = Array.isArray(response)
          ? response
          : response && Array.isArray(response.items)
          ? response.items
          : []

        this.rolesCatalog = items
      })
    },
    helperPaginate(response) {
      this.pagination.totalItems = response.total
      this.pagination.pages =
        Math.ceil(this.pagination.totalItems / this.pagination.rpp) || 0
      this.loading = false
      this.pagination.loading = false
    },
    asyncPagination() {
      this.pagination.loading = true

      const filter = {
        rpp: this.pagination.rpp,
        page: this.pagination.page,
        sortBy: this.sortBy,
        sort: this.sortDirection.toUpperCase(),
        ...this.pagination.search,
      }

      return new Promise(resolve => {
        this.$store.dispatch('users/getAll', filter).then(response => {
          this.helperPaginate(response)
          return resolve()
        })
      })
    },
    onSearch() {
      const params = {
        term: this.search,
        roleId: this.selectedRole === 'ALL' ? '' : this.selectedRole,
      }

      Object.keys(params).forEach(key => {
        if (params[key] == null || params[key] === '') delete params[key]
      })

      this.pagination.search = params
      this.pagination.page = 1
      this.asyncPagination()
    },
    openDisableModal(userId) {
      this.userDisable.modal = true
      this.userDisable.id = userId
    },
    disableUser() {
      this.userDisable.modal = false
      this.loading = true

      this.$store
        .dispatch('users/disable', { id: this.userDisable.id })
        .then(response => {
          if (response.data == 0) {
            this.loading = false
          } else {
            this.asyncPagination()
          }
        })
    },
    getRoleName(user) {
      return user && user.role && user.role.name ? user.role.name : 'SIN ROL'
    },
    isExcludedRoleName(name) {
      // Estos roles no se muestran en el listado de usuarios
      const normalized = String(name || '')
        .toUpperCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
      return (
        normalized.includes('DISTRIBUIDOR') ||
        normalized.includes('LABORATORIO')
      )
    },
    roleClass(user) {
      const roleName = this.getRoleName(user)
      const normalized = String(roleName)
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')

      if (normalized.includes('ADMIN')) return 'role-chip--admin'
      if (normalized.includes('TRAMITE')) return 'role-chip--tramite'
      if (normalized.includes('MENSAJERIA')) return 'role-chip--mensajeria'
      if (normalized.includes('DESPACHO')) return 'role-chip--despacho'
      if (normalized.includes('CONTABILIDAD')) return 'role-chip--contabilidad'
      if (normalized.includes('LABORATORIO')) return 'role-chip--laboratorio'
      if (normalized.includes('DISTRIBUIDOR')) return 'role-chip--distribuidor'

      return 'role-chip--default'
    },
    changeSort(column) {
      if (column === 'firstName') {
        this.sortBy = 'firstName'
        this.sortDirection = 'asc'
        return
      }

      if (this.sortBy === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
        return
      }

      this.sortBy = column
      this.sortDirection = 'asc'
    },
    sortIcon(column) {
      if (this.sortBy !== column) return 'unfold_more'
      return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward'
    },
    async onDownload() {
      this.exporting = true
      this.snack = {
        show: true,
        color: '#254f87',
        message: 'Generando archivo Excel...',
      }

      try {
        const response = await this.$store.dispatch('users/getAll', {
          rpp: 9999,
          page: 1,
          ...this.pagination.search,
        })

        const rows = (response && response.items) || []

        if (!rows.length) {
          this.snack = {
            show: true,
            color: 'warning',
            message: 'No hay datos para exportar.',
          }
          this.exporting = false
          return
        }

        const headers = ['Nombre', 'Apellido', 'Email', 'Rol']
        const thead = `<tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>`
        const tbody = rows
          .map(user => {
            return `<tr>
            <td>${user.firstName || ''}</td>
            <td>${user.lastName || ''}</td>
            <td>${user.email || ''}</td>
            <td>${this.getRoleName(user)}</td>
          </tr>`
          })
          .join('')

        const html = `
          <html xmlns:o="urn:schemas-microsoft-com:office:office"
                xmlns:x="urn:schemas-microsoft-com:office:excel"
                xmlns="http://www.w3.org/TR/REC-html40">
          <head><meta charset="UTF-8"></head>
          <body><table border="1">${thead}${tbody}</table></body>
          </html>`

        const blob = new Blob([html], {
          type: 'application/vnd.ms-excel;charset=utf-8;',
        })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `usuarios_${new Date().toISOString().slice(0, 10)}.xls`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)

        this.snack = {
          show: true,
          color: 'success',
          message: `${rows.length} usuarios exportados correctamente.`,
        }
      } catch (e) {
        this.snack = {
          show: true,
          color: 'error',
          message: 'Error al generar el archivo.',
        }
      } finally {
        this.exporting = false
      }
    },
  },
}
</script>

<style scoped>
.users-list-page {
  background: #f8fafc;
  min-height: 100vh;
  padding-bottom: 16px;
}

.list-hero {
  padding: 4px 0 8px;
}

.list-crumb {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.list-title {
  margin: 4px 0;
  font-size: 32px;
  line-height: 1.1;
  color: #0f172a;
  font-weight: 700;
}

.list-subtitle {
  margin: 0;
  font-size: 14px;
  color: #475569;
}

.users-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  background: #ffffff;
}

.users-card__toolbar {
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.users-card__content {
  padding: 0;
}

.table-wrap {
  width: 100%;
  overflow-x: auto;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
}

.users-table thead tr {
  background: #f8fafc;
}

.users-table th,
.users-table td {
  text-align: left;
  color: #334155;
  padding: 10px 12px;
  border-bottom: 1px solid #eef2f7;
  font-size: 14px;
}

.users-table th {
  font-size: 12px;
  letter-spacing: 0.2px;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
}

.users-table tbody tr:hover {
  background: #f8fafc;
}

.th-sort {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
  padding: 0;
  cursor: pointer;
}

.th-sort__icon {
  font-size: 14px !important;
  color: #94a3b8;
}

.th-actions {
  width: 110px;
}

.role-chip {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 12px;
  font-weight: 600;
}

.role-chip--admin {
  color: #1e3a8a;
  background: #dbeafe;
  border-color: #93c5fd;
}

.role-chip--tramite {
  color: #075985;
  background: #e0f2fe;
  border-color: #7dd3fc;
}

.role-chip--mensajeria {
  color: #0f766e;
  background: #ccfbf1;
  border-color: #5eead4;
}

.role-chip--despacho {
  color: #9a3412;
  background: #ffedd5;
  border-color: #fdba74;
}

.role-chip--contabilidad {
  color: #854d0e;
  background: #fef3c7;
  border-color: #fcd34d;
}

.role-chip--laboratorio {
  color: #166534;
  background: #dcfce7;
  border-color: #86efac;
}

.role-chip--distribuidor {
  color: #7e22ce;
  background: #f3e8ff;
  border-color: #d8b4fe;
}

.role-chip--default {
  color: #334155;
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.action-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-edit {
  border: 1px solid rgba(37, 79, 135, 0.3) !important;
  color: #254f87 !important;
  background: rgba(37, 79, 135, 0.1) !important;
  box-shadow: none !important;
}

.btn-delete {
  border: 1px solid rgba(229, 37, 42, 0.3) !important;
  color: #e5252a !important;
  background: rgba(229, 37, 42, 0.1) !important;
  box-shadow: none !important;
}

.empty-state {
  text-align: center !important;
  color: #64748b;
  padding: 18px !important;
}

.list-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.list-footer__count {
  font-size: 13px;
  color: #64748b;
}

.users-card__content-mobile {
  padding: 12px;
}

.user-mobile-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  margin-bottom: 10px;
}

.mobile-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.mobile-label {
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.mobile-value {
  color: #334155;
  font-size: 13px;
  text-align: right;
}

.mobile-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.mobile-empty {
  text-align: center;
  padding: 16px;
  color: #64748b;
  font-size: 13px;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.btn-download {
  border: 1px solid rgba(37, 79, 135, 0.3) !important;
  color: #254f87 !important;
  background: rgba(37, 79, 135, 0.1) !important;
  box-shadow: none !important;
}

@media (max-width: 959px) {
  .list-title {
    font-size: 26px;
  }

  .list-subtitle {
    font-size: 14px;
  }

  .users-card__toolbar {
    padding: 10px;
  }

  .list-footer {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
